import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import PageContent from "@/components/PageContent";
import { useToast } from "@/components/ui/use-toast";
import { QueryTable } from "@/components/query-table/QueryTable";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { Module } from "@/constants";
import { defaultDictionaryTableFeatures } from "@/containers/dictionaries/defaultDictionaryTableFeatures";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { useStoreGroupDictionary } from "@/modules/storeGroups/hooks/useStoreGroupDictionary";
import { ApiError, SuccessFetchListResponse } from "@/services/api";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFetchGroupStoresInfiniteQuery } from "../hooks/useFetchGroupStoresInfiniteQuery";
import { readStoreGroupMember } from "@/schemas/storeGroupMember";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { StoreGroupForm } from "../types";
import StoreGroupStores from "@/modules/storeGroups/components/StoreGroupStores";
import { StoreGroupSaveButton } from "./StoreGroupSaveButton";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { DataTableQuery } from "@/types/dataTableQuery";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import useQueryData from "@/hooks/useQueryData";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useStoreGroupSelection } from "../hooks/useStoreGroupSelection";
import { RowSelectionContext } from "@/components/data-table/context/RowSelectionContext";

export default function StoreGroupEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useStoreGroupDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const Icon = config.Icon;
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.StoreGroups,
  );
  const { can_update } = useModulePermissions();
  const readStoreGroupQuery = useQuery<ReadStoreGroup>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => {
      const result = await config.service.get(id);
      return result as ReadStoreGroup;
    },
  });

  const options = useCreateDataTableOptions<ReadStoreGroupMember>("stores");
  const queryKey = useDynamicQueryParams<ReadStoreGroup>(
    `storeGroupStores.${id}`,
    options,
  );
  const storesQuery = useFetchGroupStoresInfiniteQuery(id, options, queryKey);
  const stores = useQueryData(storesQuery);

  const form = useForm<StoreGroupForm>({
    resolver: zodResolver(config.schema),
    defaultValues: config.defaultValues,
    disabled: !can_update,
  });

  // Update form name and description when readStoreGroupQuery data loads
  useEffect(() => {
    if (readStoreGroupQuery.data) {
      form.setValue("name", readStoreGroupQuery.data?.name, {
        shouldDirty: false,
      });
      form.setValue("description", readStoreGroupQuery.data?.description, {
        shouldDirty: false,
      });
    }
  }, [readStoreGroupQuery.data, form]);

  const {
    store,
    getStoresToAdd,
    getStoresToDelete,
    onSaveSuccess,
    getHasSelectionChanges,
  } = useStoreGroupSelection({
    stores,
    storeGroupId: id,
    isLoaded: storesQuery.isSuccess,
  });

  const rowSelectionContextValue = useMemo(() => ({ store }), [store]);

  const mutationFn: MutationFunction<ReadStoreGroup, StoreGroupForm> =
    useCallback(
      async (values) => {
        const response = await config.service.patch<
          WriteStoreGroup,
          ReadStoreGroup
        >(
          id,
          {
            name: values.name,
            description: values.description,
            stores: {
              add: getStoresToAdd(),
              delete: getStoresToDelete(),
            },
          },
          "stores",
        );

        return {
          ...values,
          id: response?.id || id,
        };
      },
      [config.service, id, getStoresToAdd, getStoresToDelete],
    );

  const mutation = useMutationWithErrorHandling<
    ReadStoreGroup,
    ApiError,
    StoreGroupForm,
    unknown
  >({
    mutationFn,
    errorTitle: labels.errors.update,
    onSuccess: async (response) => {
      const id = response.id;
      await storesQuery.refetch();
      await updateQueryDataForExistingItem(response);
      navigate(`${config.path}/${id}`);
      toast({ title: labels.success.update });
      onSaveSuccess();
      form.reset(form.getValues());
    },
  });

  const storesQueryContextValue = useMemo<
    DataTableQuery<SuccessFetchListResponse<ReadStoreGroupMember>>
  >(
    () => ({
      query: storesQuery,
    }),
    [storesQuery],
  );

  const dictionaryRecordContext = useMemo(
    () => ({
      config,
      query: readStoreGroupQuery,
      mutation,
      form,
      id,
    }),
    [config, readStoreGroupQuery, mutation, form, id],
  );

  return (
    <DictionaryRecordContextProvider value={dictionaryRecordContext}>
      <DictionaryPageHeader />
      {readStoreGroupQuery.isFetching && <DictionaryLoader />}
      {!readStoreGroupQuery.isFetching && (
        <DictionaryForm>
          <PageContent
            icon={<Icon className="w-6 h-6 mr-3" />}
            footer={
              <StoreGroupSaveButton
                store={store}
                getHasSelectionChanges={getHasSelectionChanges}
              />
            }
            title={labels.title}
          >
            <DataTableQueryContextProvider value={storesQueryContextValue}>
              <RowSelectionContext.Provider value={rowSelectionContextValue}>
                <QueryTable
                  name={`${config.ns}.stores`}
                  schema={readStoreGroupMember}
                  query={storesQuery}
                  options={options}
                  tableFeatures={{
                    ...defaultDictionaryTableFeatures,
                    enableRowSelection: true,
                    enableColumnPinning: false,
                    exportSettings: {
                      moduleName: Module.Stores,
                      queryParams: {
                        store_group_id: id,
                      },
                    },
                    enableSelectAll: true,
                  }}
                >
                  <StoreGroupStores />
                </QueryTable>
              </RowSelectionContext.Provider>
            </DataTableQueryContextProvider>
          </PageContent>
        </DictionaryForm>
      )}
    </DictionaryRecordContextProvider>
  );
}
